import { useState, useCallback, useEffect } from 'react';
import { useCalendarStore } from '../../lib/stores/calendarStore';
import { useErrorHandler } from '../../lib/hooks/useErrorHandler';
import type { CalendarEvent } from '../../lib/types';

/**
 * Hook per l'integrazione col Calendario Microsoft (Outlook), speculare a
 * useGoogleCalendar. Fornisce gestione errori, stato di caricamento e retry.
 * Le operazioni sottostanti (calendarStore) ricavano userId/org dal JWT: qui NON
 * si passano user_id/organization_id.
 */
export function useMicrosoftCalendar() {
  const {
    microsoftEvents,
    microsoftLoading,
    microsoftError,
    fetchMicrosoftEvents,
    createMicrosoftEvent,
    updateMicrosoftEvent,
    deleteMicrosoftEvent,
    getMicrosoftEvent,
    resetErrors,
    calendarSources,
  } = useCalendarStore();

  const { handleError } = useErrorHandler();
  const [localLoading, setLocalLoading] = useState<boolean>(false);

  // Gestione errori dello store — silenzia i casi "account non collegato"
  useEffect(() => {
    if (microsoftError) {
      const isNoMicrosoftConnected =
        microsoftError.toLowerCase().includes('microsoft authentication failed') ||
        microsoftError.toLowerCase().includes('reconnect') ||
        microsoftError.toLowerCase().includes('not properly configured') ||
        microsoftError.toLowerCase().includes('edge function returned a non-2xx') ||
        microsoftError.toLowerCase().includes('404');
      if (!isNoMicrosoftConnected) {
        handleError({ message: microsoftError }, 'Microsoft Calendar error');
      }
    }
  }, [microsoftError, handleError]);

  const fetchEvents = useCallback(async (
    startDate: Date,
    endDate: Date,
    options?: { skipCalendarListFetch?: boolean; forceRefresh?: boolean }
  ) => {
    try {
      setLocalLoading(true);
      await fetchMicrosoftEvents(startDate, endDate, options);
    } catch (error) {
      handleError(error, 'Failed to fetch Microsoft Calendar events');
    } finally {
      setLocalLoading(false);
    }
  }, [fetchMicrosoftEvents, handleError]);

  /**
   * Crea un evento Outlook. `options.createTeamsMeeting` attiva un meeting Teams
   * (irreversibile); `options.activityId` marca l'evento come creato dal CRM.
   * Ritorna { event, eventId, joinUrl } per salvare microsoft_event_id / meet_link.
   */
  const createEvent = useCallback(async (
    calendarId: string | undefined,
    event: Omit<CalendarEvent, 'id' | 'created_at' | 'created_by'>,
    options?: { createTeamsMeeting?: boolean; activityId?: string; targetUserId?: string }
  ) => {
    try {
      setLocalLoading(true);
      const result = await createMicrosoftEvent(calendarId, event, options);
      if (!result) {
        throw new Error('Failed to create event');
      }
      return result;
    } catch (error) {
      handleError(error, 'Failed to create Microsoft Calendar event');
      return null;
    } finally {
      setLocalLoading(false);
    }
  }, [createMicrosoftEvent, handleError]);

  const updateEvent = useCallback(async (
    eventId: string,
    event: Partial<CalendarEvent>,
    targetUserId?: string
  ) => {
    try {
      setLocalLoading(true);
      return await updateMicrosoftEvent(eventId, event, targetUserId);
    } catch (error) {
      handleError(error, 'Failed to update Microsoft Calendar event');
      return null;
    } finally {
      setLocalLoading(false);
    }
  }, [updateMicrosoftEvent, handleError]);

  const deleteEvent = useCallback(async (eventId: string, targetUserId?: string) => {
    try {
      setLocalLoading(true);
      // delete/not_found: prosegue senza errore (gestito nel calendarStore)
      return await deleteMicrosoftEvent(eventId, targetUserId);
    } catch (error) {
      handleError(error, 'Failed to delete Microsoft Calendar event');
      throw error;
    } finally {
      setLocalLoading(false);
    }
  }, [deleteMicrosoftEvent, handleError]);

  const getEvent = useCallback(async (eventId: string) => {
    try {
      setLocalLoading(true);
      return await getMicrosoftEvent(eventId);
    } catch (error) {
      handleError(error, 'Failed to get Microsoft Calendar event');
      return null;
    } finally {
      setLocalLoading(false);
    }
  }, [getMicrosoftEvent, handleError]);

  const refreshCalendar = useCallback(async (startDate: Date, endDate: Date) => {
    resetErrors();
    return fetchEvents(startDate, endDate, { forceRefresh: true });
  }, [resetErrors, fetchEvents]);

  const getEnabledMicrosoftCalendars = useCallback(() => {
    return calendarSources.filter(
      source => source.type === 'microsoft' && source.enabled && source.microsoftCalendarId
    );
  }, [calendarSources]);

  return {
    events: microsoftEvents,
    loading: microsoftLoading || localLoading,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    getEvent,
    refreshCalendar,
    getEnabledMicrosoftCalendars,
    calendarSources: calendarSources.filter(source => source.type === 'microsoft'),
  };
}
